/**
 * Redimensionamento de tabela dentro do editor rico.
 *
 * O `contentEditable` do navegador nao oferece isso: aqui, aproximar o mouse
 * da borda de uma celula troca o cursor e permite arrastar — borda direita
 * ajusta a LARGURA da coluna inteira, borda inferior ajusta a ALTURA da linha.
 *
 * A largura vai em todas as celulas da mesma coluna (a tabela usa
 * `border-collapse`, entao mexer numa celula so desalinharia as demais).
 */

/** Distancia da borda, em px, que ativa o arraste. */
const ZONA = 5
const LARGURA_MINIMA = 40
const ALTURA_MINIMA = 24

type Arraste = {
  tipo: 'coluna' | 'linha' | 'tabela'
  celula: HTMLTableCellElement
  inicioX: number
  inicioY: number
  larguraInicial: number
  alturaInicial: number
}

/**
 * Liga o redimensionamento no elemento editavel. Devolve a funcao de limpeza.
 *
 * `aoMudar` e chamado ao soltar, para o editor persistir o novo tamanho.
 */
export function ativarRedimensionamentoDeTabela(raiz: HTMLElement, aoMudar: () => void): () => void {
  let arraste: Arraste | null = null

  const celulaDe = (alvo: EventTarget | null): HTMLTableCellElement | null => {
    if (!(alvo instanceof HTMLElement)) return null
    const celula = alvo.closest('th, td')
    return celula instanceof HTMLTableCellElement && raiz.contains(celula) ? celula : null
  }

  /**
   * Em qual borda o ponteiro esta, se em alguma.
   *
   * Na borda direita da ULTIMA coluna o arraste vale para a tabela inteira —
   * ali a borda da celula e a borda da tabela, e e onde se espera poder
   * encolher a tabela toda.
   */
  const bordaSob = (celula: HTMLTableCellElement, x: number, y: number): Arraste['tipo'] | null => {
    const area = celula.getBoundingClientRect()
    const linha = celula.parentElement
    const ehUltimaColuna = linha instanceof HTMLTableRowElement && celula.cellIndex === linha.cells.length - 1
    if (Math.abs(area.right - x) <= ZONA) return ehUltimaColuna ? 'tabela' : 'coluna'
    if (Math.abs(area.bottom - y) <= ZONA) return 'linha'
    return null
  }

  const aoMover = (evento: MouseEvent) => {
    if (arraste) {
      if (arraste.tipo === 'tabela') {
        const tabela = arraste.celula.closest('table')
        if (tabela) {
          const largura = Math.max(LARGURA_MINIMA * 2, arraste.larguraInicial + (evento.clientX - arraste.inicioX))
          // o CSS deixa a tabela em 100%; ao arrastar, a largura passa a ser
          // a escolhida — e `max-width` impede que ela estoure o editor
          tabela.style.width = `${largura}px`
          tabela.style.maxWidth = '100%'
        }
      } else if (arraste.tipo === 'coluna') {
        const largura = Math.max(LARGURA_MINIMA, arraste.larguraInicial + (evento.clientX - arraste.inicioX))
        aplicarLarguraNaColuna(arraste.celula, largura)
      } else {
        const altura = Math.max(ALTURA_MINIMA, arraste.alturaInicial + (evento.clientY - arraste.inicioY))
        const linha = arraste.celula.parentElement
        if (linha instanceof HTMLTableRowElement) linha.style.height = `${altura}px`
      }
      evento.preventDefault()
      return
    }

    // sem arraste: so o cursor indica que da para puxar
    const celula = celulaDe(evento.target)
    const borda = celula ? bordaSob(celula, evento.clientX, evento.clientY) : null
    raiz.style.cursor = borda === 'linha' ? 'row-resize' : borda ? 'col-resize' : ''
  }

  const aoPressionar = (evento: MouseEvent) => {
    const celula = celulaDe(evento.target)
    if (!celula) return
    const borda = bordaSob(celula, evento.clientX, evento.clientY)
    if (!borda) return

    const area = celula.getBoundingClientRect()
    // no modo tabela o ponto de partida e a largura da TABELA, nao da celula
    const larguraTabela = celula.closest('table')?.getBoundingClientRect().width ?? area.width
    arraste = {
      tipo: borda,
      celula,
      inicioX: evento.clientX,
      inicioY: evento.clientY,
      larguraInicial: borda === 'tabela' ? larguraTabela : area.width,
      alturaInicial: area.height,
    }
    // impede que o arraste vire selecao de texto dentro da celula
    evento.preventDefault()
    document.body.style.userSelect = 'none'
  }

  const aoSoltar = () => {
    if (!arraste) return
    arraste = null
    document.body.style.userSelect = ''
    aoMudar()
  }

  raiz.addEventListener('mousemove', aoMover)
  raiz.addEventListener('mousedown', aoPressionar)
  window.addEventListener('mousemove', aoMover)
  window.addEventListener('mouseup', aoSoltar)

  return () => {
    raiz.removeEventListener('mousemove', aoMover)
    raiz.removeEventListener('mousedown', aoPressionar)
    window.removeEventListener('mousemove', aoMover)
    window.removeEventListener('mouseup', aoSoltar)
    raiz.style.cursor = ''
  }
}

/**
 * Navegacao por Tab dentro da tabela.
 *
 * Tab vai para a proxima celula da linha e, na ultima, pula para a primeira da
 * linha seguinte; Shift+Tab faz o inverso. Fora da tabela o Tab segue o
 * comportamento normal do navegador (sair do editor).
 *
 * Devolve a funcao de limpeza.
 */
export function ativarTabNaTabela(raiz: HTMLElement, aoMudar: () => void): () => void {
  const aoTeclar = (evento: KeyboardEvent) => {
    if (evento.key !== 'Tab') return

    const selecao = window.getSelection()
    const no = selecao?.anchorNode
    if (!no) return
    const elemento = no instanceof HTMLElement ? no : no.parentElement
    const celula = elemento?.closest('th, td')
    if (!(celula instanceof HTMLTableCellElement) || !raiz.contains(celula)) return

    const tabela = celula.closest('table')
    if (!tabela) return

    // ordem de leitura: todas as celulas da tabela, linha a linha
    const celulas = Array.from(tabela.querySelectorAll<HTMLTableCellElement>('th, td'))
    const atual = celulas.indexOf(celula)
    const proxima = celulas[atual + (evento.shiftKey ? -1 : 1)]

    // Tab na ULTIMA celula cria uma linha nova e entra nela — assim da para
    // preencher a tabela inteira sem tirar a mao do teclado. Shift+Tab na
    // primeira nao cria nada: ali o Tab sai da tabela.
    if (!proxima) {
      if (evento.shiftKey) return
      evento.preventDefault()
      const nova = criarLinhaAbaixo(tabela, celula)
      if (nova) {
        focarCelula(nova)
        aoMudar()
      }
      return
    }

    evento.preventDefault()
    focarCelula(proxima)
  }

  raiz.addEventListener('keydown', aoTeclar)
  return () => raiz.removeEventListener('keydown', aoTeclar)
}

/**
 * Acrescenta uma linha no fim da tabela e devolve a primeira celula dela.
 *
 * A linha nasce com o mesmo numero de colunas e sempre com `td` (nunca `th`,
 * que e cabecalho). Cada celula leva um `<br>` para ter altura e poder receber
 * o cursor — celula vazia de verdade nao e clicavel no contentEditable.
 */
function criarLinhaAbaixo(tabela: HTMLTableElement, referencia: HTMLTableCellElement): HTMLTableCellElement | null {
  const linhaAtual = referencia.parentElement
  if (!(linhaAtual instanceof HTMLTableRowElement)) return null

  const colunas = linhaAtual.cells.length
  if (colunas === 0) return null

  const corpo = linhaAtual.parentElement ?? tabela
  const nova = document.createElement('tr')
  for (let i = 0; i < colunas; i++) {
    const celula = document.createElement('td')
    celula.appendChild(document.createElement('br'))
    // mantem a largura ja ajustada da coluna
    const modelo = linhaAtual.cells[i]
    if (modelo?.style.width) celula.style.width = modelo.style.width
    nova.appendChild(celula)
  }
  corpo.appendChild(nova)
  return nova.cells[0] ?? null
}

/** Põe o cursor no fim do conteudo da celula. */
function focarCelula(celula: HTMLTableCellElement) {
  const intervalo = document.createRange()
  intervalo.selectNodeContents(celula)
  intervalo.collapse(false)
  const selecao = window.getSelection()
  selecao?.removeAllRanges()
  selecao?.addRange(intervalo)
  celula.scrollIntoView({ block: 'nearest', inline: 'nearest' })
}

/** Aplica a largura em todas as celulas da coluna da `celula` informada. */
function aplicarLarguraNaColuna(celula: HTMLTableCellElement, largura: number) {
  const tabela = celula.closest('table')
  if (!tabela) return
  const indice = celula.cellIndex
  for (const linha of Array.from(tabela.rows)) {
    const alvo = linha.cells[indice]
    if (alvo) alvo.style.width = `${largura}px`
  }
  // sem isto o navegador redistribui as colunas e o arraste "escapa"
  tabela.style.tableLayout = 'fixed'
}
